import type { Word, WordFormInfo, WordSense } from '@/types/word'

export const SIMPLE_FORM_MAP: Record<string, string> = {
  infinitive: 'infinitive',
  infinitive_act: 'infinitive',
  infinitive_mid: 'infinitive (middle voice)',
  supine: 'past participle',
  supine_act: 'past participle',
  supine_mid: 'past participle (middle voice)',
  past_participle: 'past participle',
  past_participle_a: 'past participle',
  pres_participle: 'present participle',
  infinitive_pass: 'passive infinitive',
}

export const CASE_FRIENDLY: Record<string, string> = {
  nominative: 'subject form',
  accusative: 'object form',
  dative: 'indirect object',
  genitive: 'possessive',
}

export const CASE_LABELS: Record<string, string> = {
  nominative: 'Nominative',
  accusative: 'Accusative',
  dative: 'Dative',
  genitive: 'Genitive',
}

export const CASE_ORDER = ['nominative', 'accusative', 'dative', 'genitive']

const NUMBER_LABEL: Record<string, string> = {
  singular: 'singular',
  plural: 'plural',
}

const TENSE_LABEL: Record<string, string> = {
  present: 'present',
  past: 'past',
  future: 'future',
}

export interface FormTag {
  label: string
  hint?: string
}

export function ordinalSuffix(n: string): string {
  if (n === '1') return 'st'
  if (n === '2') return 'nd'
  if (n === '3') return 'rd'
  return 'th'
}

export function getFormTags(word: Word): FormTag[] {
  if (!word.form) return []
  const slot = word.form.grammatical_slot
  if (SIMPLE_FORM_MAP[slot]) return [{ label: SIMPLE_FORM_MAP[slot] }]

  const info = word.form.grammatical_info
  const tags: FormTag[] = []
  if (info.tense) tags.push({ label: TENSE_LABEL[info.tense] ?? info.tense })
  if (info.case) tags.push({ label: info.case, hint: CASE_FRIENDLY[info.case] })
  if (info.number) tags.push({ label: NUMBER_LABEL[info.number] ?? info.number })
  if (info.definiteness === 'definite') tags.push({ label: 'definite' })
  if (info.person) tags.push({ label: `${info.person}${ordinalSuffix(info.person)} person` })
  if (info.mood && info.mood !== 'indicative') tags.push({ label: info.mood })
  if (info.voice === 'passive') tags.push({ label: 'passive' })
  if (isComparison(info)) tags.push({ label: info.degree })
  return tags
}

/** A comparative or superlative degree; Icelandic's strong/weak degrees sit in its case tables instead. */
function isComparison(info: Record<string, string>): boolean {
  return info.degree === 'comparative' || info.degree === 'superlative'
}

export function formatSlotLabel(f: WordFormInfo): string {
  const info = f.grammatical_info
  const parts: string[] = []
  if (info.person) parts.push(`${info.person}${ordinalSuffix(info.person)} p.`)
  if (info.number) parts.push(info.number)
  if (info.case) parts.push(info.case)
  if (info.gender) parts.push(info.gender)
  if (isComparison(info)) parts.push(info.degree)
  if (info.definiteness === 'definite') parts.push('definite')
  if (info.voice) parts.push(info.voice === 'middle' ? 'middle' : 'active')
  return parts.join(', ') || f.grammatical_slot
}

/**
 * Forms with the same grammar are alternatives -- Bokmål allows "boka" and "boken"
 * alike -- so they share one entry, their surfaces joined. Ordered by slot, so the
 * base form (sg_def) leads its a-form twin (sg_def_a) whichever was stored first.
 */
export function joinAlternatives(forms: WordFormInfo[]): WordFormInfo[] {
  const byGrammar = new Map<string, WordFormInfo[]>()
  for (const f of forms) {
    const key = JSON.stringify(Object.entries(f.grammatical_info).sort())
    byGrammar.set(key, [...(byGrammar.get(key) ?? []), f])
  }
  return [...byGrammar.values()].map((alternatives) => {
    const sorted = alternatives.sort((a, b) => a.grammatical_slot.localeCompare(b.grammatical_slot))
    return { ...sorted[0], surface: sorted.map((f) => f.surface).join(' / ') }
  })
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ── Translations ─────────────────────────────────────────────────────────────

/**
 * The English glosses across all senses, each once. Wiktionary lists a verb both
 * bare and with "to", so the two spellings count as one and the bare one wins.
 */
export function uniqueTranslations(senses: Pick<WordSense, 'translations'>[], limit = 6): string[] {
  const seen = new Map<string, string>()
  for (const t of senses.flatMap((s) => s.translations.en ?? [])) {
    const bare = t.replace(/^to\s+/i, '')
    const key = bare.toLowerCase()
    if (!seen.has(key)) seen.set(key, bare)
  }
  return [...seen.values()].slice(0, limit)
}

// ── Pronouns ─────────────────────────────────────────────────────────────────

export type Person = '1' | '2' | '3'
export type GrammaticalNumber = 'singular' | 'plural'

/** Row order of a conjugation table: singular first, then plural. */
export const PERSON_ROWS: { person: Person; number: GrammaticalNumber }[] = [
  { person: '1', number: 'singular' },
  { person: '2', number: 'singular' },
  { person: '3', number: 'singular' },
  { person: '1', number: 'plural' },
  { person: '2', number: 'plural' },
  { person: '3', number: 'plural' },
]

type PronounTable = [string, string, string, string, string, string]

const ENGLISH_PRONOUNS: PronounTable = ['I', 'you', 'he / she / it', 'we', 'you (pl.)', 'they']

const PRONOUNS: Record<string, PronounTable> = {
  is: ['ég', 'þú', 'hann / hún / það', 'við', 'þið', 'þeir / þær / þau'],
  de: ['ich', 'du', 'er / sie / es', 'wir', 'ihr', 'sie / Sie'],
  es: ['yo', 'tú', 'él / ella / usted', 'nosotros / nosotras', 'vosotros / vosotras', 'ellos / ellas / ustedes'],
  nb: ['jeg', 'du', 'han / hun / det', 'vi', 'dere', 'de'],
  nn: ['eg', 'du', 'han / ho / det', 'vi', 'de', 'dei'],
  sv: ['jag', 'du', 'han / hon / det', 'vi', 'ni', 'de'],
  da: ['jeg', 'du', 'han / hun / det', 'vi', 'I', 'de'],
}

/** The subject pronoun a form goes with, in the word's own language where known. */
export function pronounFor(iso: string | undefined, person: Person, number: GrammaticalNumber): string {
  const table = (iso && PRONOUNS[iso]) || ENGLISH_PRONOUNS
  return table[PERSON_ROWS.findIndex((r) => r.person === person && r.number === number)]
}

/** The English pronoun for a row, for the hint behind a foreign one. */
export function englishPronoun(person: Person, number: GrammaticalNumber): string {
  return pronounFor(undefined, person, number)
}

// ── Form descriptions ────────────────────────────────────────────────────────

const SEP = ' · '

/** The formal name of a slot, spelled out: "3rd person singular · present indicative · active voice". */
export function describeForm(f: WordFormInfo): string {
  const info = f.grammatical_info
  const parts: string[] = []
  if (info.person) parts.push(`${info.person}${ordinalSuffix(info.person)} person${info.number ? ` ${info.number}` : ''}`)
  else if (info.number) parts.push(info.number)
  if (info.verb_form) parts.push(info.tense ? `${info.tense} ${info.verb_form}` : info.verb_form)
  else parts.push([info.tense, info.mood].filter(Boolean).join(' '))
  if (info.voice) parts.push(`${info.voice} voice`)
  return parts.filter(Boolean).join(SEP)
}

// ── Verb paradigm ────────────────────────────────────────────────────────────

export interface VerbGroup {
  key: string
  label: string
  /** Finite groups are keyed by pronoun; the rest is a short list of named forms. */
  finite: boolean
  forms: WordFormInfo[]
}

const TENSE_ORDER = ['present', 'past', 'imperfect', 'perfect', 'pluperfect', 'future', 'future I', 'future II']
const MOOD_ORDER = ['indicative', 'subjunctive', 'subjunctive I', 'subjunctive II', 'conditional', 'imperative']
const VOICE_ORDER = ['active', 'middle', 'passive']

const NONFINITE_KEY = 'nonfinite'

function rank(order: string[], value: string | undefined): number {
  const i = order.indexOf(value ?? order[0])
  return i === -1 ? order.length : i
}

/** Non-finite forms, and the finite forms of a verb with no person endings (Bokmål), go in a list. */
function isListed(info: Record<string, string>): boolean {
  return Boolean(info.verb_form) || (!info.person && !info.number)
}

function groupKey(info: Record<string, string>): string {
  if (isListed(info)) return `${NONFINITE_KEY}|${info.voice ?? 'active'}`
  return [info.voice ?? 'active', info.mood ?? 'indicative', info.tense ?? ''].join('|')
}

function voiceSuffix(info: Record<string, string>): string {
  return info.voice && info.voice !== 'active' ? `, ${info.voice} voice` : ''
}

function groupLabel(info: Record<string, string>): string {
  if (isListed(info)) return `Other forms${voiceSuffix(info)}`
  if (info.mood === 'imperative') return `Imperative${voiceSuffix(info)}`
  const mood = info.mood && info.mood !== 'indicative' ? info.mood : ''
  // Spanish's conditional is a mood without a tense: the mood alone names the table.
  const name = [info.tense, mood].filter(Boolean).join(' ')
  return capitalize(`${name}${voiceSuffix(info)}`)
}

function groupRank(info: Record<string, string>): number {
  if (isListed(info)) return 100_000 + rank(VOICE_ORDER, info.voice)
  return rank(VOICE_ORDER, info.voice) * 1000 + rank(MOOD_ORDER, info.mood) * 100 + rank(TENSE_ORDER, info.tense)
}

/** Buckets a verb's forms into tense/mood/voice tables, in the order a grammar prints them. */
export function groupVerbForms(forms: WordFormInfo[]): VerbGroup[] {
  const groups = new Map<string, VerbGroup & { rank: number }>()
  for (const f of forms) {
    const info = f.grammatical_info
    const key = groupKey(info)
    const g = groups.get(key) ?? {
      key,
      label: groupLabel(info),
      finite: !key.startsWith(NONFINITE_KEY),
      forms: [],
      rank: groupRank(info),
    }
    g.forms.push(f)
    groups.set(key, g)
  }
  return [...groups.values()]
    .sort((a, b) => a.rank - b.rank)
    .map(({ rank: _rank, ...g }) => (g.finite ? g : asList(g)))
}

/**
 * A list in the order a grammar prints principal parts, alternatives on one line.
 * Holding finite forms makes it the verb's principal parts rather than its "other" forms.
 */
function asList(g: VerbGroup): VerbGroup {
  const ordered = [...g.forms].sort((a, b) => listRank(a.grammatical_info) - listRank(b.grammatical_info))
  const forms = joinAlternatives(ordered)
  const info = forms[0].grammatical_info
  if (forms.every((f) => f.grammatical_info.verb_form)) return { ...g, forms }
  const voice = info.voice ?? 'active'
  return { ...g, forms, label: voice === 'active' ? 'Principal parts' : capitalize(`${voice} voice`) }
}

/** infinitive, present, past, participles and supine, imperative, present participle */
function listRank(info: Record<string, string>): number {
  if (info.verb_form === 'infinitive') return 0
  if (!info.verb_form) return info.mood === 'imperative' ? 4 : 1 + rank(TENSE_ORDER, info.tense)
  return info.tense === 'present' ? 5 : 3
}

/** The tables a learner meets first: indicative and imperative in the active voice, plus the non-finite forms. */
export function isBasicVerbGroup(g: VerbGroup): boolean {
  const info = g.forms[0]?.grammatical_info ?? {}
  if (info.voice && info.voice !== 'active') return false
  if (isListed(info)) return true
  if (info.mood === 'imperative') return true
  return (info.mood ?? 'indicative') === 'indicative' && ['present', 'past'].includes(info.tense ?? '')
}

/**
 * Spelled-out name of a listed form: "infinitive", "past participle", "supine" -- or,
 * for a verb with no person endings, its tense or mood: "present", "imperative".
 */
export function nonFiniteLabel(f: WordFormInfo): string {
  const info = f.grammatical_info
  const base = (info.verb_form ?? info.mood ?? (info.tense ? '' : f.grammatical_slot)).replace(/_/g, ' ')
  const name = info.tense && !base.includes(info.tense) ? `${info.tense} ${base}`.trim() : base
  // A Spanish participle agrees with its noun, so its four forms differ only by these.
  return info.gender ? `${name}, ${info.gender} ${info.number}` : name
}

/**
 * German paradigms spell the pronoun into the form ("ich quietsche"); beside a
 * pronoun column that reads twice, so the copy inside the cell goes.
 */
export function stripLeadingPronoun(surface: string, pronoun: string): string {
  for (const p of pronoun.split(' / ')) {
    if (surface.toLowerCase().startsWith(`${p.toLowerCase()} `)) return surface.slice(p.length + 1)
  }
  return surface
}
